import React from 'react';
import { InternalId } from '@edgebox/data-definition-kit';
import { MonitoringPromptType } from '@edgebox/sync-core-data-definitions';
import { ClientMonitoringPromptEntity } from '@edgebox/sync-core-rest-client';
import { SyncCoreApiContext } from '../../../../contexts/SyncCoreApiContext';

interface IMonitoringPromptInstanceNameProps {
  prompt: ClientMonitoringPromptEntity;
  template?: ClientMonitoringPromptEntity;
  localeNameByKey?: Map<string, string>;
  termNameByKey?: Map<string, string>;
}

function getStableKey(reference: unknown): string {
  if (!reference || typeof reference !== 'object') {
    return '';
  }

  const referenceRecord = reference as {
    getStableKey?: () => string;
    key?: unknown;
  };

  return String(referenceRecord.getStableKey?.() || referenceRecord.key || '').trim();
}

function getAssignmentTermStableKey(assignment: unknown): string {
  if (!assignment || typeof assignment !== 'object') {
    return '';
  }

  const assignmentRecord = assignment as {
    term?: unknown;
  };

  return getStableKey(assignmentRecord.term);
}

export function MonitoringPromptInstanceName({ prompt, template, localeNameByKey, termNameByKey }: IMonitoringPromptInstanceNameProps) {
  const api = React.useContext(SyncCoreApiContext)?.api;
  const [resolvedTemplate, setResolvedTemplate] = React.useState<ClientMonitoringPromptEntity | undefined>(template);
  const [templateLoading, setTemplateLoading] = React.useState(false);

  React.useEffect(() => {
    setResolvedTemplate(template);
  }, [template]);

  React.useEffect(() => {
    if (prompt.type !== MonitoringPromptType.Instance || template || resolvedTemplate) {
      return;
    }

    let active = true;

    void (async () => {
      try {
        setTemplateLoading(true);

        const templatePromptReference = prompt.templatePrompt as
          | {
              get?: () => Promise<ClientMonitoringPromptEntity>;
              getId?: () => unknown;
            }
          | undefined;

        if (templatePromptReference?.get) {
          const loadedTemplate = await templatePromptReference.get();
          if (!active) {
            return;
          }

          setResolvedTemplate(loadedTemplate);
          return;
        }

        const templatePromptId = templatePromptReference?.getId?.();
        if (api && templatePromptId !== undefined && templatePromptId !== null) {
          const loadedTemplate = await api.governance.monitoringPrompts.item(templatePromptId as InternalId, true);
          if (!active) {
            return;
          }

          setResolvedTemplate(loadedTemplate);
        }
      } catch {
        if (!active) {
          return;
        }

        setResolvedTemplate(undefined);
      } finally {
        if (active) {
          setTemplateLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [api, prompt, resolvedTemplate, template]);

  if (prompt.type !== MonitoringPromptType.Instance) {
    return <>{prompt.name}</>;
  }

  if (!resolvedTemplate?.name) {
    return <>{templateLoading ? 'Loading template…' : 'Template unavailable'}</>;
  }

  const baseName = resolvedTemplate.name;
  const localeKey = getStableKey(prompt.promptLocale);
  const localeLabel = localeKey ? localeNameByKey?.get(localeKey) || localeKey : '';

  const instanceTermKeys = Array.from(
    new Set(
      [
        ...(prompt.targetTerms || []).map((assignment) => getAssignmentTermStableKey(assignment)).filter((key) => !!key),
        ...(prompt.staticTerms || []).map((term) => getStableKey(term)).filter((key) => !!key),
      ].filter((key) => !!key)
    )
  );

  const templateTermKeys = new Set((resolvedTemplate.templateTerms || []).map((term) => getStableKey(term)).filter((key) => !!key));

  const matchedTermKey =
    templateTermKeys.size > 0
      ? instanceTermKeys.find((termKey) => templateTermKeys.has(termKey)) || instanceTermKeys[0]
      : instanceTermKeys[0];

  const matchedTermLabel = matchedTermKey ? termNameByKey?.get(matchedTermKey) || matchedTermKey : '';
  const suffix = matchedTermLabel && localeLabel ? `${matchedTermLabel} in ${localeLabel}` : matchedTermLabel || localeLabel;

  return <>{suffix ? `${baseName} (${suffix})` : baseName}</>;
}
